import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Mail, Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  // Forgot password state
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      if (data.reset_url) setResetUrl(data.reset_url);
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // Reset Password Form (when token is present)
  if (token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] p-8" data-testid="reset-password-page">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="flex items-center justify-center mb-8">
            <Shield className="w-10 h-10 text-[#B76E79] mr-3" strokeWidth={1.5} />
            <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
          </div>

          <div className="bg-white p-8 border border-[#36454F]/15">
            {resetDone ? (
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-[#4CAF50] mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">Password Reset!</h2>
                <p className="text-[#36454F]/65 font-['Manrope'] mb-6">Your password has been updated successfully.</p>
                <Link to="/login">
                  <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="goto-login-btn">
                    Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">Set New Password</h2>
                <p className="text-[#36454F]/65 mb-6 font-['Manrope']">Choose a new password for your account</p>

                {error && (
                  <div className="mb-4 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm font-['Manrope']" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-[#36454F] font-['Manrope'] font-medium">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                      <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']" required data-testid="new-password-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#36454F] font-['Manrope'] font-medium">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                      <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']" required data-testid="confirm-password-input" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm" data-testid="reset-password-btn">
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password Form
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex items-center justify-center mb-8">
          <Shield className="w-10 h-10 text-[#B76E79] mr-3" strokeWidth={1.5} />
          <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
        </div>

        <div className="bg-white p-8 border border-[#36454F]/15">
          {sent ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-[#4CAF50] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">Check Your Email</h2>
              <p className="text-[#36454F]/65 font-['Manrope'] mb-4">
                If an account exists with that email, we've sent reset instructions.
              </p>
              {resetUrl && (
                <div className="p-3 bg-[#E0BFB8]/10 border border-[#E0BFB8]/30 rounded-sm mb-4">
                  <p className="text-xs text-[#36454F]/50 font-['Manrope'] mb-1">Demo mode — your reset link:</p>
                  <a href={resetUrl} className="text-xs text-[#B76E79] font-['Manrope'] break-all hover:underline" data-testid="reset-link">
                    {resetUrl}
                  </a>
                </div>
              )}
              <Link to="/login">
                <Button variant="outline" className="border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <Link to="/login" className="inline-flex items-center text-[#36454F]/50 hover:text-[#36454F] font-['Manrope'] text-sm mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />Back to Sign In
              </Link>

              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">Forgot Password?</h2>
              <p className="text-[#36454F]/65 mb-6 font-['Manrope']">Enter your email and we'll help you reset it</p>

              {error && (
                <div className="mb-4 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm font-['Manrope']" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-[#36454F] font-['Manrope'] font-medium">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']" required data-testid="forgot-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm" data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-[#36454F]/30 font-['Manrope']">
          © {new Date().getFullYear()} Selah &amp; Stone Legacy Holdings LLC · All rights reserved
        </p>
      </div>
    </div>
  );
}
